"use client";

import { useState, useTransition, useOptimistic } from "react";
import { track } from "@/lib/analytics";

interface VoteButtonProps {
  toolId: number;
  initialVotes: number;
}

export default function VoteButton({
  toolId,
  initialVotes,
}: VoteButtonProps) {
  const [hasVoted, setHasVoted] = useState(false);
  const [votes, setVotes] = useState(initialVotes);
  const [isPending, startTransition] = useTransition();

  const [optimisticState, setOptimistic] = useOptimistic(
    { votes, hasVoted },
    (current) => ({
      votes: current.votes + 1,
      hasVoted: true,
    })
  );

  const handleClick = () => {
    if (optimisticState.hasVoted) return;

    startTransition(async () => {
      setOptimistic(null);

      try {
        const response = await fetch("/api/vote", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ toolId, type: "upvote" }),
        });

        if (response.ok) {
          setVotes((v) => v + 1);
          setHasVoted(true);
          // GA4 conversion: user upvoted a tool.
          track("tool_vote", { tool_id: toolId });
        }
      } catch {
        // Optimistic state will revert since actual state wasn't updated
      }
    });
  };

  return (
    <button
      onClick={handleClick}
      disabled={isPending || optimisticState.hasVoted}
      className={`group inline-flex flex-col items-center gap-0.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
        optimisticState.hasVoted
          ? "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
          : "border-gray-200 bg-white text-gray-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-blue-800 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
      } ${isPending ? "opacity-70" : ""}`}
      aria-label={
        optimisticState.hasVoted
          ? `Voted (${optimisticState.votes} votes)`
          : `Upvote (${optimisticState.votes} votes)`
      }
    >
      <span
        className={`text-xs leading-none transition-transform ${
          optimisticState.hasVoted
            ? "text-blue-600 dark:text-blue-400"
            : "text-gray-400 group-hover:text-blue-600 dark:text-gray-500 dark:group-hover:text-blue-400"
        }`}
        aria-hidden="true"
      >
        ▲
      </span>
      <span className="leading-none">{optimisticState.votes}</span>
    </button>
  );
}
